import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { GruppeTier } from '../classes/gruppe-tier';
import { Tierreklamation } from '../classes/tierreklamation';
import { GruppeTierService } from '../gruppe-tier.service';
import { MessageService } from '../../../services/message-service/message.service';
import { StammdatenService } from '../../stammdaten/stammdaten.service';
import { MatSelectionListChange } from '@angular/material/list';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { EnterValueDialogComponent } from '../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../dialogs/enter-value-dialog/enter-value-response';

@Component({
    selector: 'kiene-gruppe-tier-reklamationen-setzen-dialog',
    templateUrl: './gruppe-tier-reklamationen-setzen-dialog.component.html',
    styleUrls: ['./gruppe-tier-reklamationen-setzen-dialog.component.scss'],
})
export class GruppeTierReklamationenSetzenDialogComponent implements OnInit, OnDestroy {
    isSaving = false;
    isCreating = false;
    isLoading = true;
    reklamationen: Tierreklamation[] = [];
    selectedIds = new Set<number>();
    canCreateTierreklamation = false;
    private pendingCreatedReklamationText: string | null = null;

    private readonly subscriptions: Subscription[] = [];

    constructor(
        private readonly dialogRef: MatDialogRef<GruppeTierReklamationenSetzenDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { tiere: GruppeTier[]; gruppeId?: number },
        private readonly dialog: MatDialog,
        private readonly gruppeTierService: GruppeTierService,
        private readonly stammdatenService: StammdatenService,
        private readonly messageService: MessageService,
        private readonly localStorageService: LocalStorageService,
    ) {}

    ngOnInit(): void {
        this.canCreateTierreklamation = this.currentUserCanCreateTierreklamation();
        this.loadReklamationen();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    toggleSelection(reklamation: Tierreklamation, checked: boolean): void {
        if (checked) {
            this.selectedIds.add(reklamation.tierreklamation_id);
        } else {
            this.selectedIds.delete(reklamation.tierreklamation_id);
        }
    }

    removeAll(): void {
        this.selectedIds.clear();
    }

    save(): void {
        if (!this.data?.tiere?.length) {
            this.messageService.errorMessage('Keine Tiere ausgewählt!', null);
            return;
        }

        this.isSaving = true;
        this.gruppeTierService
            .updateReklamationenMultiple(this.data.tiere, Array.from(this.selectedIds), this.data.gruppeId)
            .subscribe({
            next: success => {
                this.isSaving = false;
                if (!success) {
                    this.dialogRef.close(false);
                    return;
                }
                this.messageService.infoMessage('Reklamationen für alle ausgewählten Tiere aktualisiert.');
                this.dialogRef.close(true);
            },
            error: err => {
                this.isSaving = false;
                this.messageService.errorMessage('Reklamationen konnten nicht aktualisiert werden!', err);
            },
        });
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    openCreateTierreklamationDialog(): void {
        if (!this.canCreateTierreklamation || this.isCreating) {
            return;
        }

        const input = new EnterValueInput();
        input.title = 'Tierreklamation anlegen';
        input.addInputValue('Bezeichnung', 'text');

        this.dialog
            .open(EnterValueDialogComponent, { data: input })
            .afterClosed()
            .subscribe({
                next: (result: EnterValueResponse) => {
                    const text = result?.getValue(0)?.trim?.();
                    if (!text) {
                        return;
                    }
                    this.isCreating = true;
                    this.pendingCreatedReklamationText = text;
                    this.stammdatenService.addTierreklamation(text).subscribe({
                        next: () => {
                            this.isCreating = false;
                            this.loadReklamationen();
                        },
                        error: err => {
                            this.isCreating = false;
                            this.pendingCreatedReklamationText = null;
                            this.messageService.errorMessage('Fehler beim Anlegen der Tierreklamation', err);
                        },
                    });
                },
            });
    }

    isChecked(reklamation: Tierreklamation): boolean {
        return this.selectedIds.has(reklamation.tierreklamation_id);
    }

    onSelectionChange(event: MatSelectionListChange): void {
        if (!event.options?.length) {
            return;
        }

        for (const option of event.options) {
            const reklamation = option.value as Tierreklamation;
            if (!reklamation) {
                continue;
            }
            this.toggleSelection(reklamation, option.selected);
        }
    }

    private loadReklamationen(): void {
        this.isLoading = true;
        const sub = this.stammdatenService.getTierreklamationen(true).subscribe({
            next: response => {
                this.reklamationen = response?.records || [];
                if (this.pendingCreatedReklamationText) {
                    const created = this.reklamationen.find(
                        r => (r?.reklamation ?? '').trim() === this.pendingCreatedReklamationText,
                    );
                    if (created?.tierreklamation_id) {
                        this.selectedIds.add(created.tierreklamation_id);
                    }
                    this.pendingCreatedReklamationText = null;
                }
                this.isLoading = false;
            },
            error: err => {
                this.isLoading = false;
                this.messageService.errorMessage('Tierreklamationen konnten nicht geladen werden!', err);
            },
        });
        this.subscriptions.push(sub);
    }

    private currentUserCanCreateTierreklamation(): boolean {
        return this.localStorageService.hasPermission(9232);
    }
}
